import React, { CSSProperties, useEffect, useMemo, useRef } from 'react';
import QueueHeader from '../../components/Main/QueueHeader/QueueHeader';
import QueuePanel from '../../components/Main/QueuePanel/QueuePanel';
import classes from './QueuePage.module.scss';
import { useGetQueueTickers } from '../../hooks/MonitorQueue/useGetQueueTickers';
import { isQueueScreenDarkTheme } from '../../constants/API';
import { FooterAsterixIcon } from '../../assets/svgComponents/FooterAsterixIcon';
import classNames from 'classnames';
import { ClientLicenseHasExpiredModal } from '../../components/LicenseModals/LicenseHasExpiredModal/ClientLicenseHasExpiredModal';
import { useGetQueueTerminalAppearance } from '../../hooks/MonitorQueue/useGetQueueTerminalAppearance';
import { TerminalTabloElementSettings } from '../../types/Admin/TerminalAppearanceTypes';
import { TabloSlider } from '../../components/Main/TabloSlider/TabloSlider';
import { WeatherWidget } from '../../components/Main/WeatherWidget/WeatherWidget';

const QueuePage = () => {
  const { queueTickersResp } = useGetQueueTickers();
  const { terminalAppearanceResponse, isLoadingTerminalAppearance } =
    useGetQueueTerminalAppearance();
  const tickerMarqueeRef = useRef<HTMLDivElement | null>(null);
  const tickerTrackRef = useRef<HTMLDivElement | null>(null);
  const isInitialAppearanceLoading = isLoadingTerminalAppearance && !terminalAppearanceResponse;
  const tabloSettings = terminalAppearanceResponse?.settings?.tablo ?? null;
  const layoutEnabled = !!tabloSettings;
  const backgrounds = terminalAppearanceResponse?.backgrounds ?? [];
  const headerElement = tabloSettings?.elements?.header ?? null;
  const tickerElement = tabloSettings?.elements?.ticker ?? null;
  const sliderElement = tabloSettings?.elements?.slider ?? null;
  const weatherWidgetElement = tabloSettings?.elements?.weatherWidget ?? null;
  const isHeaderEnabled = headerElement?.isEnabled ?? true;
  const isTickerEnabled = tickerElement?.isEnabled ?? true;
  const isSliderEnabled = sliderElement?.isEnabled ?? false;
  const isWeatherWidgetEnabled = weatherWidgetElement?.isEnabled ?? false;

  const tabloResolutionStyle = useMemo<CSSProperties>(() => {
    if (!layoutEnabled) return {};
    const w = Number(tabloSettings?.resolutionWidth) || 1920;
    const h = Number(tabloSettings?.resolutionHeight) || 1080;
    return {
      aspectRatio: `${w} / ${h}`,
      height: '100%',
      width: 'auto',
      maxWidth: '100%',
      maxHeight: '100%'
    };
  }, [layoutEnabled, tabloSettings?.resolutionWidth, tabloSettings?.resolutionHeight]);

  const activeBackground = useMemo(() => {
    if (!tabloSettings?.backgroundId) {
      return null;
    }
    return backgrounds.find((item) => item.id === tabloSettings.backgroundId) ?? null;
  }, [backgrounds, tabloSettings?.backgroundId]);

  const buildElementStyle = (
    element?: TerminalTabloElementSettings | null
  ): CSSProperties | undefined => {
    if (!layoutEnabled || !element) {
      return undefined;
    }
    const blur = Number.isFinite(element.backdropBlur) ? Number(element.backdropBlur) : null;
    return {
      position: 'absolute',
      left: `${element.x}%`,
      top: `${element.y}%`,
      width: `${element.width}%`,
      height: `${element.height}%`,
      zIndex: 1,
      backgroundColor: element.backgroundColor || undefined,
      backdropFilter: blur !== null ? `blur(${blur}px)` : undefined,
      WebkitBackdropFilter: blur !== null ? `blur(${blur}px)` : undefined
    };
  };

  const backgroundStyle = useMemo<CSSProperties>(() => {
    if (!layoutEnabled) {
      return {};
    }
    return {
      backgroundColor: tabloSettings?.backgroundColor || undefined
    };
  }, [layoutEnabled, tabloSettings?.backgroundColor]);

  const tickerTextStyle = useMemo<CSSProperties>(() => {
    const durationSeconds =
      terminalAppearanceResponse?.settings?.tickerSpeed &&
      Number.isFinite(terminalAppearanceResponse.settings.tickerSpeed)
        ? terminalAppearanceResponse.settings.tickerSpeed
        : 50;
    return {
      fontFamily: tickerElement?.fontFamily || undefined,
      fontWeight: tickerElement?.fontWeight || undefined,
      fontSize: tickerElement?.fontSize ? `${tickerElement.fontSize}px` : undefined,
      color: tickerElement?.textColor || undefined,
      ['--ticker-animation-duration' as string]: `${durationSeconds}s`
    };
  }, [
    terminalAppearanceResponse?.settings?.tickerSpeed,
    tickerElement?.fontFamily,
    tickerElement?.fontWeight,
    tickerElement?.fontSize,
    tickerElement?.textColor
  ]);

  useEffect(() => {
    const trackEl = tickerTrackRef.current;
    const outerEl = tickerMarqueeRef.current;
    if (!trackEl || !outerEl) return;
    if (!isTickerEnabled) return;
    if (!queueTickersResp?.tickers?.length) return;

    const container = outerEl.parentElement;
    if (!container) return;

    const travelSecondsRaw = terminalAppearanceResponse?.settings?.tickerSpeed;
    const travelSeconds =
      travelSecondsRaw && Number.isFinite(travelSecondsRaw) ? Number(travelSecondsRaw) : 50;

    const pauseSecondsStart = 5;
    const pauseSecondsEnd = 5;
    const totalSeconds = Math.max(0.1, pauseSecondsStart + travelSeconds + pauseSecondsEnd);
    const startMoveOffset = Math.min(0.999, Math.max(0.001, pauseSecondsStart / totalSeconds));
    const endMoveOffset = Math.min(
      0.999,
      Math.max(0.001, (pauseSecondsStart + travelSeconds) / totalSeconds)
    );

    let animation: Animation | null = null;
    let cancelled = false;

    const snapPx = (value: number) => {
      const dpr =
        typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1;
      return Math.round(value * dpr) / dpr;
    };

    const start = () => {
      animation?.cancel();

      const containerWidth = Math.max(0, container.getBoundingClientRect().width);
      const textWidth = Math.max(0, trackEl.getBoundingClientRect().width);
      const edgePadding = 24;
      const startX = snapPx(containerWidth + edgePadding);
      const endX = snapPx(-(textWidth + edgePadding));

      animation = trackEl.animate(
        [
          { transform: `translate3d(${startX}px, 0, 0)`, offset: 0 },
          { transform: `translate3d(${startX}px, 0, 0)`, offset: startMoveOffset },
          { transform: `translate3d(${endX}px, 0, 0)`, offset: endMoveOffset },
          { transform: `translate3d(${endX}px, 0, 0)`, offset: 1 }
        ],
        { duration: totalSeconds * 1000, iterations: Infinity, easing: 'linear' }
      );
    };

    // Ждём шрифты ДО первого запуска, чтобы ширина текста не "прыгнула" в середине движения.
    const fonts: FontFaceSet | undefined = (document as any).fonts;
    const maybeStart = async () => {
      try {
        if (fonts?.status && fonts.status !== 'loaded' && fonts.ready) {
          await fonts.ready;
        }
      } finally {
        if (!cancelled) start();
      }
    };
    void maybeStart();

    return () => {
      cancelled = true;
      animation?.cancel();
    };
  }, [
    isTickerEnabled,
    queueTickersResp?.tickers?.length,
    terminalAppearanceResponse?.settings?.tickerSpeed
  ]);
  const backgroundObjectFit = useMemo(() => {
    switch (tabloSettings?.backgroundFitMode) {
      case 'contain':
        return 'contain';
      case 'stretch':
        return 'fill';
      case 'width':
      case 'height':
        return 'contain';
      case 'none':
        return 'none';
      case 'cover':
      default:
        return 'cover';
    }
  }, [tabloSettings?.backgroundFitMode]);

  const layoutContent = (
    <>
      {layoutEnabled && activeBackground && (
        <div className={classes.layoutBackground}>
          {activeBackground.mediaType === 'VIDEO' ? (
            <video
              className={classes.layoutBackgroundMedia}
              style={{ objectFit: backgroundObjectFit }}
              autoPlay
              muted
              loop
              playsInline
              src={activeBackground.url}
            />
          ) : (
            <img
              className={classes.layoutBackgroundMedia}
              style={{ objectFit: backgroundObjectFit }}
              src={activeBackground.url}
              alt=""
            />
          )}
        </div>
      )}
      {isHeaderEnabled && (
        <QueueHeader
          logoSettings={
            layoutEnabled && terminalAppearanceResponse?.settings?.tablo?.logo != null
              ? terminalAppearanceResponse.settings.tablo.logo
              : terminalAppearanceResponse?.settings?.logo ?? null
          }
          tabloHeader={headerElement}
          containerStyle={buildElementStyle(headerElement)}
        />
      )}
      <QueuePanel
        tabloSettings={tabloSettings ?? undefined}
        layoutMode={layoutEnabled}
        videoVolumeOverride={terminalAppearanceResponse?.settings?.videoVolume ?? null}
      />
      {layoutEnabled && isSliderEnabled && sliderElement && (
        <div className={classes.layoutItem} style={buildElementStyle(sliderElement)}>
          <TabloSlider element={sliderElement} />
        </div>
      )}
      {layoutEnabled && isWeatherWidgetEnabled && weatherWidgetElement && (
        <div className={classes.layoutItem} style={buildElementStyle(weatherWidgetElement)}>
          <WeatherWidget element={weatherWidgetElement} />
        </div>
      )}
      {isTickerEnabled && (
        <footer
          className={classNames(classes.footerContainer, {
            [classes.darkThemeFooterContainer]: isQueueScreenDarkTheme,
            [classes.layoutItem]: layoutEnabled
          })}
          style={buildElementStyle(tickerElement)}>
          {queueTickersResp && (
            <div
              ref={tickerMarqueeRef}
              className={classes.footerMarquee}
              style={tickerTextStyle}>
              <div ref={tickerTrackRef} className={classes.footerMarqueeTrack}>
                <span
                  className={classNames(classes.footerText, {
                    [classes.darkThemeFooterText]: isQueueScreenDarkTheme
                  })}>
                  {queueTickersResp.tickers.map((e, idx) => (
                    <span className={classes.runningString} key={idx}>
                      <FooterAsterixIcon />
                      <span>{e.text}</span>
                    </span>
                  ))}
                </span>
              </div>
            </div>
          )}
        </footer>
      )}
    </>
  );

  if (isInitialAppearanceLoading) {
    return (
      <div className={classes.queuePageContainer}>
        <ClientLicenseHasExpiredModal />
      </div>
    );
  }

  return (
    <div
      className={classNames(classes.queuePageContainer, {
        [classes.darkQueuePageContainer]: isQueueScreenDarkTheme && !layoutEnabled,
        [classes.layoutEnabled]: layoutEnabled
      })}
      style={!layoutEnabled ? backgroundStyle : undefined}>
      {layoutEnabled ? (
        <div className={classes.tabloLayoutWrapper}>
          <div
            className={classes.tabloLayout}
            style={{ ...tabloResolutionStyle, ...backgroundStyle }}>
            {layoutContent}
          </div>
        </div>
      ) : (
        layoutContent
      )}

      <ClientLicenseHasExpiredModal />
    </div>
  );
};

export default QueuePage;
